import React, { useState, useRef, useEffect } from 'react';
import VinDisplay from './shared/VinDisplay';

const INSPECTION_PASSWORD = '4015';
const API_URL = process.env.REACT_APP_API_URL || 'https://lotsense-production.up.railway.app/api';

// Simple fetch wrapper that doesn't require auth (no redirect on 401)
const inspectionApi = {
  async startInspection(data) {
    const res = await fetch(`${API_URL}/ai-inspection/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to start inspection');
    return res.json();
  },
  async getVehicleDetails(vehicleId) {
    const res = await fetch(`${API_URL}/ai-inspection/vehicle/${vehicleId}`);
    if (!res.ok) throw new Error('Failed to fetch vehicle details');
    return res.json();
  },
  async uploadFile(formData) {
    const res = await fetch(`${API_URL}/ai-inspection/upload-file`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Failed to upload file');
    return res.json();
  },
  async markDelivered(vehicleId) {
    const res = await fetch(`${API_URL}/ai-inspection/mark-delivered`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ vehicleId }),
    });
    if (!res.ok) throw new Error('Failed to mark delivered');
    return res.json();
  },
  async completeInspection(data) {
    const res = await fetch(`${API_URL}/ai-inspection/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to complete inspection');
    return res.json();
  },
  async saveReport(data) {
    const res = await fetch(`${API_URL}/ai-inspection/save-report`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Failed to save report');
    return res.json();
  },
};

// Auction Light Display Component
const AuctionLights = ({ lights }) => {
  if (!lights) return <span className="text-neutral-400">None</span>;
  const lightArray = lights.split(',').map(l => l.trim().toLowerCase());
  return (
    <div className="flex gap-2 items-center">
      {lightArray.includes('green') && <span className="text-2xl" title="Green Light">🟢</span>}
      {lightArray.includes('yellow') && <span className="text-2xl" title="Yellow Light">🟡</span>}
      {lightArray.includes('red') && <span className="text-2xl" title="Red Light">🔴</span>}
      {lightArray.length === 0 && <span className="text-neutral-400">None</span>}
    </div>
  );
};

// End Use Badge Component
const EndUseBadge = ({ endUse }) => {
  const badges = {
    wholesale: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Wholesale' },
    byrider: { bg: 'bg-red-100', text: 'text-red-700', label: 'Byrider' },
    dealer_direct: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Dealer Direct' },
  };
  const badge = badges[endUse] || badges.wholesale;
  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
      {badge.label}
    </span>
  );
};

const AIInspectionFlow = ({ vehicle, stockNumber, onBack }) => {
  // Screen states: password, info, checklist, uploads, summary, processing, complete
  const [screen, setScreen] = useState('password');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [inspectorName, setInspectorName] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [startTime, setStartTime] = useState(null);
  
  // Vehicle data (enhanced from API)
  const [vehicleData, setVehicleData] = useState(null);
  const [partsData, setPartsData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Inspection type
  const [isByRiderOrDD, setIsByRiderOrDD] = useState(false);
  const [hasGreenLight, setHasGreenLight] = useState(false);
  
  // Checklist (for Byrider/DD only)
  const [checklist, setChecklist] = useState({
    testDrive: false,
    hardAcceleration: false,
    liftInspection: false,
    fluidLeaks: false,
    underbodyVideo: false,
    structureDamage: false,
  });
  
  // Uploads
  const [bluedriverFiles, setBluedriverFiles] = useState([]);
  const [photoFiles, setPhotoFiles] = useState([]);
  const [videoFiles, setVideoFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  
  // Summary
  const [techNotes, setTechNotes] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [greenLightIssue, setGreenLightIssue] = useState(false);
  const recognitionRef = useRef(null);
  
  // Results
  const [completionData, setCompletionData] = useState(null);
  const [processingStep, setProcessingStep] = useState(0);
  const [error, setError] = useState(null);

  // Fetch enhanced vehicle data when entering info screen
  useEffect(() => {
    if (screen === 'info' && !vehicleData) {
      setLoading(true);
      inspectionApi.getVehicleDetails(vehicle.id)
        .then(data => {
          setVehicleData(data.vehicle);
          setPartsData(data.parts || []);
          // Determine if Byrider/DD based on end_use
          const endUse = data.vehicle?.endUse || '';
          const isByriderDD = endUse === 'byrider' || endUse === 'dealer_direct';
          setIsByRiderOrDD(isByriderDD);
          // Check for green light
          const lights = data.vehicle?.auctionLight || '';
          setHasGreenLight(lights.toLowerCase().includes('green'));
          // Pre-check structure damage if flagged
          if (data.vehicle?.structureDamage) {
            setChecklist(prev => ({ ...prev, structureDamage: true }));
          }
        })
        .catch(err => {
          console.error('Failed to load vehicle details:', err);
          // Fall back to props data
          setVehicleData(vehicle);
          setIsByRiderOrDD(vehicle.byrider || vehicle.dealerDirect);
        })
        .finally(() => setLoading(false));
    }
  }, [screen, vehicle, vehicleData]);

  // Password submission
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (password !== INSPECTION_PASSWORD) {
      setPasswordError('Invalid code, try again');
      return;
    }
    
    // Log start time and create session
    const now = new Date();
    setStartTime(now);
    
    // Start session in background - don't wait for it
    inspectionApi.startInspection({
      vehicleId: vehicle.id,
      stockNumber: stockNumber,
      inspectorName: inspectorName || 'Unknown',
      startTime: now.toISOString(),
    }).then(response => {
      if (response?.sessionId) {
        setSessionId(response.sessionId);
      }
    }).catch(err => {
      console.error('Failed to start inspection session:', err);
    });
    
    setScreen('info');
  };

  // Mark as delivered - actually updates the vehicle record
  const handleMarkDelivered = async () => {
    try {
      await inspectionApi.markDelivered(vehicle.id);
      setVehicleData(prev => ({ ...prev, deliveryStatus: 'delivered' }));
      alert('Vehicle marked as delivered!');
    } catch (err) {
      console.error('Failed to mark delivered:', err);
      alert('Failed to mark as delivered: ' + err.message);
    }
  };

  // File upload handlers - uploads to server and saves to vehicle_files
  const handleFileUpload = async (files, type) => {
    const fileArray = Array.from(files);
    
    for (const file of fileArray) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('vehicleId', vehicle.id);
        formData.append('category', type);
        if (sessionId) formData.append('sessionId', sessionId);
        
        const result = await inspectionApi.uploadFile(formData);
        
        if (type === 'bluedriver') {
          setBluedriverFiles(prev => [...prev, { name: file.name, uploaded: true, url: result.fileUrl }]);
        } else if (type === 'photo') {
          setPhotoFiles(prev => [...prev, { name: file.name, uploaded: true, url: result.fileUrl }]);
        } else if (type === 'video') {
          setVideoFiles(prev => [...prev, { name: file.name, uploaded: true, url: result.fileUrl }]);
        }
      } catch (err) {
        console.error('Upload failed:', err);
        alert('Failed to upload ' + file.name);
      }
    }
  };

  // Voice recording
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      alert('Speech recognition not supported in this browser. Please use Chrome.');
      return;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    
    recognitionRef.current.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setTechNotes(transcript);
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
    };
    
    recognitionRef.current.onend = () => {
      setIsRecording(false);
    };
    
    recognitionRef.current.start();
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  };

  // Complete inspection
  const handleCompleteInspection = async () => {
    setScreen('processing');
    setProcessingStep(1);
    
    try {
      // Step 1: Gathering data
      setProcessingStep(1);
      await new Promise(r => setTimeout(r, 500));
      
      // Step 2: Processing uploads
      setProcessingStep(2);
      await new Promise(r => setTimeout(r, 500));
      
      // Step 3: Creating RO
      setProcessingStep(3);
      await new Promise(r => setTimeout(r, 500));
      
      // Step 4: Saving inspection
      setProcessingStep(4);
      
      const endTime = new Date();
      const duration = Math.round((endTime - startTime) / 1000 / 60); // minutes
      
      // Collect photo URLs
      const photoUrls = photoFiles.filter(f => f.url).map(f => f.url);
      const videoUrls = videoFiles.filter(f => f.url).map(f => f.url);
      
      // Get BlueDriver data text if available
      const bluedriverData = bluedriverFiles.length > 0 
        ? `${bluedriverFiles.length} scan file(s) uploaded: ${bluedriverFiles.map(f => f.name).join(', ')}`
        : null;
      
      const response = await inspectionApi.completeInspection({
        sessionId,
        vehicleId: vehicle.id,
        stockNumber,
        inspectorName,
        endTime: endTime.toISOString(),
        duration,
        inspectionType: isByRiderOrDD ? 'byrider_dd' : 'standard',
        checklist: isByRiderOrDD ? checklist : null,
        techNotes,
        bluedriverData,
        photoUrls,
        videoUrls,
        greenLightIssue,
        vehicleData: {
          year: vehicleData?.year || vehicle.year,
          make: vehicleData?.make || vehicle.make,
          model: vehicleData?.model || vehicle.model,
          vin: vehicleData?.vin || vehicle.vin,
          mileage: vehicleData?.mileage || vehicle.mileage,
          stockNumber: stockNumber,
          reconLevel: vehicleData?.reconLevel,
          structureDamage: vehicleData?.structureDamage,
          endUse: vehicleData?.endUse,
          auctionLight: vehicleData?.auctionLight,
        }
      });
      
      setCompletionData(response);
      setScreen('complete');
      
    } catch (err) {
      console.error('Inspection completion failed:', err);
      setError(err.message);
      setScreen('complete');
    }
  };

  // Done - save and exit
  const handleDone = async () => {
    try {
      // Save AI analysis to vehicle record
      if (aiAnalysis) {
        await inspectionApi.saveReport({
          vehicleId: vehicle.id,
          sessionId,
          analysis: aiAnalysis,
        });
      }
    } catch (err) {
      console.error('Failed to save report:', err);
    }
    onBack();
  };

  // Render screens
  const renderPasswordScreen = () => (
    <div className="max-w-md mx-auto text-center py-12">
      <div className="w-16 h-16 mx-auto mb-6 bg-indigo-100 rounded-full flex items-center justify-center">
        <span className="text-3xl">🔐</span>
      </div>
      <h1 className="text-xl font-bold text-neutral-800 mb-2">Enter Access Code</h1>
      <p className="text-neutral-500 text-sm mb-6">Inspection for {vehicle.year} {vehicle.make} {vehicle.model}</p>
      
      <form onSubmit={handlePasswordSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            placeholder="Inspector Name (optional)"
            value={inspectorName}
            onChange={(e) => setInspectorName(e.target.value)}
            className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-center text-lg"
          />
        </div>
        <div>
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="4-digit code"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
            className="w-full px-4 py-4 border border-neutral-300 rounded-lg text-center text-2xl tracking-widest"
            autoFocus
          />
          {passwordError && (
            <p className="text-red-500 text-sm mt-2">{passwordError}</p>
          )}
        </div>
        <button
          type="submit"
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-4 px-6 rounded-lg transition"
        >
          Submit
        </button>
      </form>
      
      <button
        onClick={onBack}
        className="mt-6 text-neutral-500 hover:text-neutral-700 text-sm"
      >
        ← Back
      </button>
    </div>
  );

  const renderInfoScreen = () => (
    <div className="max-w-lg mx-auto py-6 px-4">
      <h1 className="text-xl font-bold text-neutral-800 mb-4 text-center">Inspection Info</h1>
      
      {loading ? (
        <div className="text-center py-8 text-neutral-500">Loading vehicle data...</div>
      ) : (
        <div className="space-y-4">
          {/* Vehicle Header */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <div className="text-center mb-3">
              <h2 className="text-lg font-bold">{vehicleData?.year || vehicle.year} {vehicleData?.make || vehicle.make} {vehicleData?.model || vehicle.model}</h2>
              <p className="font-mono text-neutral-500">Stock #{stockNumber}</p>
            </div>
            <div className="flex justify-center mb-3">
              <EndUseBadge endUse={vehicleData?.endUse || 'wholesale'} />
            </div>
          </div>

          {/* Auction Info */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="font-semibold text-neutral-700 mb-3">Auction Information</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-neutral-500 block text-xs">Auction Lights</span>
                <AuctionLights lights={vehicleData?.auctionLight} />
              </div>
              <div>
                <span className="text-neutral-500 block text-xs">Recon Level</span>
                <p className="font-bold text-lg">{vehicleData?.reconLevel || 'N/A'}</p>
              </div>
              <div>
                <span className="text-neutral-500 block text-xs">Structure Damage</span>
                <p className={`font-medium ${vehicleData?.structureDamage ? 'text-red-600' : 'text-green-600'}`}>
                  {vehicleData?.structureDamage ? '⚠️ YES' : '✓ None'}
                </p>
              </div>
              <div>
                <span className="text-neutral-500 block text-xs">VIN</span>
                <VinDisplay vin={vehicleData?.vin || vehicle.vin} />
              </div>
            </div>
            {vehicleData?.auctionAnnouncements && (
              <div className="mt-3 pt-3 border-t">
                <span className="text-neutral-500 block text-xs mb-1">Auction Announcements</span>
                <p className="text-sm bg-amber-50 p-2 rounded border border-amber-200">{vehicleData.auctionAnnouncements}</p>
              </div>
            )}
          </div>

          {/* Delivery Status Alert */}
          {vehicleData?.deliveryStatus !== 'delivered' && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">⚠️</span>
                <span className="font-medium text-amber-800">Vehicle not marked as delivered</span>
              </div>
              <button
                onClick={handleMarkDelivered}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white font-medium py-3 px-6 rounded-lg transition"
              >
                Mark as Delivered
              </button>
            </div>
          )}

          {/* Parts Status */}
          <div className="bg-white border border-neutral-200 rounded-lg p-4">
            <h3 className="font-semibold text-neutral-700 mb-2">Parts Status</h3>
            {partsData.length > 0 ? (
              <div>
                <div className="flex items-center gap-2 text-blue-700 mb-2">
                  <span>📦</span>
                  <span className="font-medium">{partsData.length} part{partsData.length !== 1 ? 's' : ''} on order</span>
                </div>
                <ul className="text-sm space-y-1 max-h-32 overflow-y-auto">
                  {partsData.map((part, i) => (
                    <li key={i} className="flex justify-between text-neutral-600">
                      <span>{part.description}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        part.status === 'arrived' ? 'bg-green-100 text-green-700' :
                        part.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                        'bg-neutral-100 text-neutral-600'
                      }`}>{part.status}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p className="text-neutral-500 text-sm">No parts ordered yet</p>
            )}
          </div>

          {/* Inspection Type Alert */}
          <div className={`rounded-lg p-4 border-2 ${isByRiderOrDD ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
            {isByRiderOrDD ? (
              <div className="text-center">
                <span className="text-3xl mb-2 block">⚠️</span>
                <h3 className="font-bold text-red-700">DEALER DIRECT / BYRIDER</h3>
                <p className="text-red-600 text-sm">THOROUGH INSPECTION REQUIRED</p>
                <p className="text-xs text-neutral-600 mt-1">Full checklist will be shown</p>
              </div>
            ) : (
              <div className="text-center">
                <span className="text-3xl mb-2 block">✅</span>
                <h3 className="font-bold text-green-700">STANDARD WHOLESALE</h3>
                <p className="text-green-600 text-sm">Quick inspection</p>
              </div>
            )}
          </div>
        </div>
      )}
      
      <button
        onClick={() => setScreen(isByRiderOrDD ? 'checklist' : 'uploads')}
        disabled={loading}
        className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-6 rounded-lg transition disabled:opacity-50 text-lg"
      >
        Continue
      </button>
      
      <button
        onClick={onBack}
        className="w-full mt-3 text-neutral-500 hover:text-neutral-700 py-2"
      >
        ← Back
      </button>
    </div>
  );

  const renderChecklistScreen = () => {
    const allChecked = Object.values(checklist).every(v => v);
    
    return (
      <div className="max-w-lg mx-auto py-8">
        <h1 className="text-xl font-bold text-neutral-800 mb-2 text-center">Inspection Checklist</h1>
        <p className="text-neutral-500 text-sm text-center mb-6">Byrider / Dealer Direct - All items required</p>
        
        <div className="bg-white border border-neutral-200 rounded-lg divide-y">
          {[
            { key: 'testDrive', label: '20 minute test drive completed' },
            { key: 'hardAcceleration', label: 'Hard acceleration test performed' },
            { key: 'liftInspection', label: 'Lift inspection - checked suspension/undercarriage' },
            { key: 'fluidLeaks', label: 'Checked for fluid leaks' },
            { key: 'underbodyVideo', label: 'Recorded underbody video' },
            { key: 'structureDamage', label: 'Structure damage check', disabled: vehicleData?.structureDamage },
          ].map(item => (
            <label key={item.key} className="flex items-center p-4 cursor-pointer hover:bg-neutral-50">
              <input
                type="checkbox"
                checked={checklist[item.key]}
                onChange={(e) => setChecklist(prev => ({ ...prev, [item.key]: e.target.checked }))}
                disabled={item.disabled}
                className="w-6 h-6 rounded border-neutral-300 text-purple-600 focus:ring-purple-500"
              />
              <span className={`ml-3 ${checklist[item.key] ? 'text-green-700' : 'text-neutral-700'}`}>
                {item.label}
                {item.disabled && <span className="text-xs text-neutral-400 ml-2">(auto-checked from system)</span>}
              </span>
            </label>
          ))}
        </div>
        
        <button
          onClick={() => setScreen('uploads')}
          disabled={!allChecked}
          className={`w-full mt-6 font-medium py-4 px-6 rounded-lg transition ${
            allChecked 
              ? 'bg-indigo-600 hover:bg-indigo-700 text-white' 
              : 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
          }`}
        >
          {allChecked ? 'Continue' : 'Complete all items to continue'}
        </button>
      </div>
    );
  };

  const renderUploadsScreen = () => (
    <div className="max-w-lg mx-auto py-8">
      <h1 className="text-xl font-bold text-neutral-800 mb-6 text-center">Upload Inspection Files</h1>
      
      <div className="space-y-6">
        {/* BlueDriver Scan */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <h3 className="font-medium text-neutral-800 mb-2">📄 BlueDriver Scan (PDF)</h3>
          <p className="text-xs text-neutral-500 mb-3">Includes OBD codes and smog monitor status</p>
          <input
            type="file"
            accept=".pdf,image/*"
            multiple
            onChange={(e) => handleFileUpload(e.target.files, 'bluedriver')}
            className="hidden"
            id="bluedriver-upload"
          />
          <label
            htmlFor="bluedriver-upload"
            className="block w-full py-3 px-4 bg-blue-50 border-2 border-dashed border-blue-300 rounded-lg text-center text-blue-600 cursor-pointer hover:bg-blue-100"
          >
            Tap to Upload Scan
          </label>
          {bluedriverFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {bluedriverFiles.map((f, i) => (
                <div key={i} className="text-xs text-green-600 flex items-center gap-1">
                  ✓ {f.name}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Photos */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <h3 className="font-medium text-neutral-800 mb-2">📷 Photos</h3>
          <input
            type="file"
            accept="image/*"
            multiple
            capture="environment"
            onChange={(e) => handleFileUpload(e.target.files, 'photo')}
            className="hidden"
            id="photo-upload"
          />
          <label
            htmlFor="photo-upload"
            className="block w-full py-3 px-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg text-center text-green-600 cursor-pointer hover:bg-green-100"
          >
            Tap to Take/Upload Photos
          </label>
          {photoFiles.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {photoFiles.map((f, i) => (
                <div key={i} className="w-16 h-16 bg-neutral-100 rounded overflow-hidden">
                  {f.url && <img src={f.url} alt="" className="w-full h-full object-cover" />}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Videos */}
        <div className="bg-white border border-neutral-200 rounded-lg p-4">
          <h3 className="font-medium text-neutral-800 mb-2">🎥 Videos</h3>
          {isByRiderOrDD && (
            <p className="text-xs text-amber-600 mb-2">⚠️ Include underbody video for Byrider/DD</p>
          )}
          <input
            type="file"
            accept="video/*"
            multiple
            capture="environment"
            onChange={(e) => handleFileUpload(e.target.files, 'video')}
            className="hidden"
            id="video-upload"
          />
          <label
            htmlFor="video-upload"
            className="block w-full py-3 px-4 bg-amber-50 border-2 border-dashed border-amber-300 rounded-lg text-center text-amber-600 cursor-pointer hover:bg-amber-100"
          >
            Tap to Record/Upload Videos
          </label>
          {videoFiles.length > 0 && (
            <div className="mt-2 space-y-1">
              {videoFiles.map((f, i) => (
                <div key={i} className="text-xs text-green-600 flex items-center gap-1">
                  ✓ {f.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <button
        onClick={() => setScreen('summary')}
        className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-4 px-6 rounded-lg transition"
      >
        Continue
      </button>
    </div>
  );

  const renderSummaryScreen = () => (
    <div className="max-w-lg mx-auto py-6 px-4">
      <h1 className="text-xl font-bold text-neutral-800 mb-2 text-center">What did you find?</h1>
      <p className="text-neutral-500 text-sm text-center mb-6">Speak or type your findings</p>
      
      {/* Voice Input */}
      <div className="mb-4">
        <button
          onClick={isRecording ? stopRecording : startRecording}
          className={`w-full py-5 rounded-lg font-medium text-lg flex items-center justify-center gap-3 transition ${
            isRecording 
              ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' 
              : 'bg-indigo-100 hover:bg-indigo-200 text-indigo-700'
          }`}
        >
          <span className="text-2xl">{isRecording ? '⏹️' : '🎤'}</span>
          {isRecording ? 'TAP TO STOP' : 'TAP TO SPEAK'}
        </button>
        {isRecording && (
          <p className="text-center text-sm text-red-500 mt-2 animate-pulse">Recording...</p>
        )}
      </div>
      
      {/* Text Input */}
      <div className="mb-4">
        <textarea
          value={techNotes}
          onChange={(e) => setTechNotes(e.target.value)}
          placeholder="Repairs needed, parts to order, any issues found..."
          rows={5}
          className="w-full px-4 py-3 border border-neutral-300 rounded-lg resize-none text-base"
        />
      </div>

      {/* Green Light Issue Alert */}
      {hasGreenLight && (
        <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={greenLightIssue}
              onChange={(e) => setGreenLightIssue(e.target.checked)}
              className="w-6 h-6 mt-0.5 rounded border-green-400 text-green-600 focus:ring-green-500"
            />
            <div>
              <span className="font-medium text-green-800 flex items-center gap-2">
                <span>🟢</span> Issues found on Green Light vehicle
              </span>
              <p className="text-xs text-green-700 mt-1">
                Check this if you found problems. Admin will be alerted immediately.
              </p>
            </div>
          </label>
        </div>
      )}
      
      <button
        onClick={handleCompleteInspection}
        className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-6 rounded-lg transition text-lg"
      >
        Complete Inspection
      </button>
      
      <button
        onClick={() => setScreen('uploads')}
        className="w-full mt-3 text-neutral-500 hover:text-neutral-700 py-2"
      >
        ← Back to Uploads
      </button>
    </div>
  );

  const renderProcessingScreen = () => {
    const steps = [
      { num: 1, label: 'Gathering inspection data...' },
      { num: 2, label: 'Processing uploads...' },
      { num: 3, label: 'Creating repair order...' },
      { num: 4, label: 'Saving inspection...' },
    ];
    
    return (
      <div className="max-w-lg mx-auto py-12 text-center">
        <div className="text-5xl mb-6 animate-bounce">🤖</div>
        <h1 className="text-xl font-bold text-neutral-800 mb-8">Analyzing inspection data...</h1>
        
        <div className="space-y-3 text-left max-w-xs mx-auto">
          {steps.map(step => (
            <div key={step.num} className="flex items-center gap-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm ${
                processingStep > step.num ? 'bg-green-500 text-white' :
                processingStep === step.num ? 'bg-indigo-500 text-white animate-pulse' :
                'bg-neutral-200 text-neutral-400'
              }`}>
                {processingStep > step.num ? '✓' : step.num}
              </span>
              <span className={processingStep >= step.num ? 'text-neutral-800' : 'text-neutral-400'}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCompleteScreen = () => (
    <div className="max-w-lg mx-auto py-8 px-4 text-center">
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
          <span className="text-4xl mb-4 block">❌</span>
          <h1 className="text-xl font-bold text-red-700 mb-2">Inspection Error</h1>
          <p className="text-red-600">{error}</p>
          <button
            onClick={onBack}
            className="mt-4 text-blue-600 hover:underline"
          >
            Go Back
          </button>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <span className="text-6xl mb-4 block">✅</span>
            <h1 className="text-2xl font-bold text-green-700 mb-2">Inspection Complete!</h1>
            <p className="text-neutral-600">
              {vehicleData?.year || vehicle.year} {vehicleData?.make || vehicle.make} {vehicleData?.model || vehicle.model}
            </p>
          </div>

          {completionData?.roNumber && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-blue-700 font-medium">RO Created</p>
              <p className="text-2xl font-bold text-blue-800">{completionData.roNumber}</p>
            </div>
          )}

          {greenLightIssue && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-4 mb-4">
              <p className="text-green-700 font-medium flex items-center justify-center gap-2">
                <span>🟢</span> Green Light Alert Sent
              </p>
              <p className="text-sm text-green-600">Admin has been notified of the issues found.</p>
            </div>
          )}

          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-neutral-700 mb-2">Summary</h3>
            <p className="text-sm text-neutral-600 whitespace-pre-wrap">{techNotes || 'No notes recorded'}</p>
            <div className="mt-3 pt-3 border-t text-xs text-neutral-500">
              <p>Inspector: {inspectorName || 'Unknown'}</p>
              <p>Duration: {completionData?.duration || '?'} minutes</p>
              <p>Files: {bluedriverFiles.length + photoFiles.length + videoFiles.length} uploaded</p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={onBack}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 px-6 rounded-lg transition text-lg"
            >
              Back to Vehicle
            </button>
            <button
              onClick={() => window.location.reload()}
              className="w-full bg-neutral-200 hover:bg-neutral-300 text-neutral-700 font-medium py-3 px-6 rounded-lg transition"
            >
              Scan Next QR
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Main render
  return (
    <div className="min-h-screen bg-neutral-50">
      {screen === 'password' && renderPasswordScreen()}
      {screen === 'info' && renderInfoScreen()}
      {screen === 'checklist' && renderChecklistScreen()}
      {screen === 'uploads' && renderUploadsScreen()}
      {screen === 'summary' && renderSummaryScreen()}
      {screen === 'processing' && renderProcessingScreen()}
      {screen === 'complete' && renderCompleteScreen()}
    </div>
  );
};

export default AIInspectionFlow;
